import { useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

// Types
import type { WatchlistItem, ModalType, SortColumn, SortDirection } from './types';

// Hooks
import {
  useWatchlist,
  useChecklistIds,
  useProcessedItems,
  useWatchlistStats,
  useWatchlistMutations,
} from './hooks/useWatchlistData';

// Components
import { WatchlistHero } from './components/WatchlistHero';
import { WatchlistTable } from './components/WatchlistTable';
import { LimitWarning } from './components/LimitWarning';
import { EmptyState } from './components/EmptyState';

// Modals
import { AddProductModal, ItemDetailModal } from './components/modals';

export default function Watchlist() {
  const { t } = useLanguage();

  // Core state
  const [modal, setModal] = useState<ModalType>('none');
  const [filter, setFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedItem, setSelectedItem] = useState<WatchlistItem | null>(null);

  // Data fetching
  const { data, isLoading, error, refetch } = useWatchlist();

  const items = data?.items || [];
  const limit = data?.limit || 5;
  const used = data?.used || items.length;

  // Get all unique checklist IDs for consistent coloring
  const allChecklistIds = useChecklistIds(items);

  // Process items with filter and sort
  const processedItems = useProcessedItems(items, filter, sortColumn, sortDirection);

  // Calculate stats for hero
  const stats = useWatchlistStats(items);

  // Mutations
  const mutations = useWatchlistMutations({
    onAddSuccess: () => {
      setModal('none');
    },
    onRemoveSuccess: () => {
      setModal('none');
      setSelectedItem(null);
    },
  });

  // Handlers
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleItemClick = (item: WatchlistItem) => {
    setSelectedItem(item);
    setModal('item-detail');
  };

  const handleAddProduct = (productCode: string) => {
    mutations.addMutation.mutate(productCode);
  };

  const handleRemoveItem = () => {
    if (!selectedItem) return;
    mutations.removeMutation.mutate(selectedItem.productCode);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-24">
        <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          {t('watchlist.error.title', 'Failed to load watchlist')}
        </h2>
        <p className="text-gray-600 mt-2">
          {t('watchlist.error.message', 'Please try again later')}
        </p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-6">
      {/* Hero Section */}
      <WatchlistHero
        stats={stats}
        filter={filter}
        onFilterChange={setFilter}
        onAddClick={() => setModal('add-product')}
        used={used}
        limit={limit}
      />

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Limit Warning */}
        <LimitWarning used={used} limit={limit} />

        {/* Content */}
        {items.length === 0 ? (
          <EmptyState
            type="no-items"
            onAddClick={() => setModal('add-product')}
          />
        ) : processedItems.length === 0 ? (
          <EmptyState
            type="no-matches"
            onClearFilter={() => setFilter('')}
          />
        ) : (
          <WatchlistTable
            items={processedItems}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            onItemClick={handleItemClick}
            onRemove={(productCode) => mutations.removeMutation.mutate(productCode)}
            allChecklistIds={allChecklistIds}
          />
        )}
      </div>

      {/* Modals */}
      <AddProductModal
        isOpen={modal === 'add-product'}
        onClose={() => setModal('none')}
        onSubmit={handleAddProduct}
        isPending={mutations.addMutation.isPending}
        isError={mutations.addMutation.isError}
      />

      <ItemDetailModal
        isOpen={modal === 'item-detail'}
        onClose={() => { setModal('none'); setSelectedItem(null); }}
        item={selectedItem}
        onRemove={handleRemoveItem}
        isRemoving={mutations.removeMutation.isPending}
        allChecklistIds={allChecklistIds}
      />
    </div>
  );
}
